import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Save } from 'lucide-react';

const MenuEditModal = ({ menu, isOpen, onClose, onSave }) => {
  const [editedMenu, setEditedMenu] = useState(menu);

  const handleFieldChange = (field, value) => {
    setEditedMenu(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedFieldChange = (category, field, value) => {
    setEditedMenu(prev => ({
      ...prev,
      [category]: { ...prev[category], [field]: value }
    }));
  };

  const handleSave = () => {
    onSave(editedMenu);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif text-primary">
            Modifier le menu
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Season */}
          <div>
            <Label htmlFor="season" className="text-sm font-medium text-gray-700">
              Saison
            </Label>
            <Input
              id="season"
              value={editedMenu.season}
              onChange={(e) => handleFieldChange('season', e.target.value)}
              placeholder="ex: HIVER, PRINTEMPS, ÉTÉ, AUTOMNE"
              className="mt-1"
            />
          </div>

          {/* Amuse-bouche */}
          <div>
            <Label htmlFor="amuse-bouche" className="text-sm font-medium text-gray-700">
              Amuse-bouche
            </Label>
            <Input
              id="amuse-bouche"
              value={editedMenu.amuse_bouche}
              onChange={(e) => handleFieldChange('amuse_bouche', e.target.value)}
              placeholder="ex: 6 Mises en bouche"
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Entrée */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-serif text-primary">
                  Entrée
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="appetizer-name" className="text-sm text-gray-600">
                    Nom de l'entrée
                  </Label>
                  <Input
                    id="appetizer-name"
                    value={editedMenu.appetizer.name}
                    onChange={(e) => handleNestedFieldChange('appetizer', 'name', e.target.value)}
                    placeholder="Nom de l'entrée"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="appetizer-description" className="text-sm text-gray-600">
                    Description
                  </Label>
                  <Textarea
                    id="appetizer-description"
                    value={editedMenu.appetizer.description}
                    onChange={(e) => handleNestedFieldChange('appetizer', 'description', e.target.value)}
                    placeholder="Description de l'entrée"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="appetizer-wine" className="text-sm text-gray-600">
                    Vin blanc
                  </Label>
                  <Textarea
                    id="appetizer-wine"
                    value={editedMenu.appetizer.wine}
                    onChange={(e) => handleNestedFieldChange('appetizer', 'wine', e.target.value)}
                    placeholder="Vin blanc pour l'entrée"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Plat */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-serif text-primary">
                  Plat principal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="main-name" className="text-sm text-gray-600">
                    Nom du plat
                  </Label>
                  <Input
                    id="main-name"
                    value={editedMenu.main.name}
                    onChange={(e) => handleNestedFieldChange('main', 'name', e.target.value)}
                    placeholder="Nom du plat principal"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="main-description" className="text-sm text-gray-600">
                    Description
                  </Label>
                  <Textarea
                    id="main-description"
                    value={editedMenu.main.description}
                    onChange={(e) => handleNestedFieldChange('main', 'description', e.target.value)}
                    placeholder="Description du plat principal"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="main-wine" className="text-sm text-gray-600">
                    Vin rouge
                  </Label>
                  <Textarea
                    id="main-wine"
                    value={editedMenu.main.wine}
                    onChange={(e) => handleNestedFieldChange('main', 'wine', e.target.value)}
                    placeholder="Vin rouge pour le plat principal"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Dessert */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-serif text-primary">
                  Dessert
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="dessert-name" className="text-sm text-gray-600">
                    Nom du dessert
                  </Label>
                  <Input
                    id="dessert-name"
                    value={editedMenu.dessert.name}
                    onChange={(e) => handleNestedFieldChange('dessert', 'name', e.target.value)}
                    placeholder="Nom du dessert"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dessert-description" className="text-sm text-gray-600">
                    Description
                  </Label>
                  <Textarea
                    id="dessert-description"
                    value={editedMenu.dessert.description}
                    onChange={(e) => handleNestedFieldChange('dessert', 'description', e.target.value)}
                    placeholder="Description du dessert"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Price and note */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price" className="text-sm font-medium text-gray-700">
                Prix
              </Label>
              <Input
                id="price"
                value={editedMenu.price}
                onChange={(e) => handleFieldChange('price', e.target.value)}
                placeholder="ex: 60.00 €"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="note" className="text-sm font-medium text-gray-700">
                Note
              </Label>
              <Input
                id="note"
                value={editedMenu.note}
                onChange={(e) => handleFieldChange('note', e.target.value)}
                placeholder="ex: Menu all in, boissons comprises"
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSave} className="btn-primary">
            <Save className="h-4 w-4 mr-2" />
            Sauvegarder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MenuEditModal;